import { captureRecruitmentContext } from '~/utils/recruitmentContext'
import Vue from 'vue'
import { serializeLocationPayload } from '~/utils/location'
import { normalizeSalaryCurrency } from '~/utils/salary'
import { reportWorkflowError } from '~/utils/workflowErrorReporting'
import { hasOrganizationLinkedInCompany } from '~/utils/linkedinCompany'

// Tracks which job offer IDs have already had personality_profile_defined fired
// this session, to prevent duplicate events on repeated saves.
const trackedPersonalityJobIds = new Set()

// Test-only escape hatch: call in beforeEach to prevent cross-test coupling
// caused by the module-level Set being cached across Jest tests in the same worker.
export const _resetTrackedPersonalityIds = () =>
	trackedPersonalityJobIds.clear()

const trackPersonalityProfileDefined = ({
	app,
	rootState,
	context,
	jobOffer
}) => {
	if (!process.client || !app.$analytics) {
		return
	}

	if (!context.track_personality_profile_defined) {
		return
	}

	const user = rootState.user?.user
	const organizationId = user?.current_organization?.id
	if (!organizationId) {
		return
	}

	const jobOfferId = jobOffer?.id
	if (!jobOfferId || trackedPersonalityJobIds.has(jobOfferId)) {
		return
	}

	try {
		app.$analytics.trackPersonalityProfileDefined({
			organizationId,
			userId: user?.id,
			jobOfferId,
			sourceSurface: context.source_surface || 'job_edit_personality'
		})
		trackedPersonalityJobIds.add(jobOfferId)
	} catch {
		// Analytics failure must not pollute the save error boundary.
	}
}

const hasLocationFields = jobOffer =>
	[
		'location_type',
		'country_id',
		'region',
		'city',
		'zip',
		'town_id',
		'outside_location',
		'provinces',
		'province_id',
		'province'
	].some(field => Object.prototype.hasOwnProperty.call(jobOffer || {}, field))

async function updateLifecycle(
	{ dispatch, commit, rootState },
	payload,
	data,
	operation
) {
	const isCurrentContext = captureRecruitmentContext(rootState)
	const id = payload?.id || payload
	const suppressToast = Boolean(payload?.suppressToast)
	const successToast = payload?.successToast || null
	let updated

	try {
		updated = await this.$axios.$patch(`/job-offer/${id}`, data, {
			__skipGlobalErrorToast: true
		})
	} catch (error) {
		if (!isCurrentContext()) return null
		console.error('Error updating job offer lifecycle:', error)
		reportWorkflowError(error, {
			flow: 'job_offer_management',
			operation,
			jobOfferId: id,
			rootState,
			reportValidationError: true
		})

		const validationErrors = error.response?.data?.errors || {}
		const validationMessages = Object.keys(validationErrors).reduce(
			(messages, field) => [
				...messages,
				...(Array.isArray(validationErrors[field])
					? validationErrors[field]
					: [validationErrors[field]])
			],
			[]
		)

		dispatch(
			'toast/push',
			{
				title: this.app.i18n.t('general.error'),
				message: validationMessages.length
					? validationMessages
					: this.app.i18n.t('crud.update-failed'),
				type: 'danger'
			},
			{ root: true }
		)

		throw error
	}

	if (!isCurrentContext()) return null
	if (!suppressToast) {
		dispatch(
			'toast/push',
			successToast || {
				title: updated.name || this.app.i18n.t('crud.updated'),
				message: this.app.i18n.t('crud.updated-text'),
				type: 'success'
			},
			{ root: true }
		)
	}

	commit('SET_JOB_OFFER_BY_ID', updated)

	return updated
}

function invalidateJobAccess(commit, id) {
	commit('REMOVE_JOB_OFFER', id)
	commit('SET_SINGLE_JOB_OFFER', null)
	commit('application/RESET_STATE', null, { root: true })
}

function applyCanonicalAccess(commit, updated) {
	if (updated.capabilities?.view_job === false) {
		invalidateJobAccess(commit, updated.id)
		return
	}
	commit('SET_JOB_OFFER_BY_ID', updated)
	commit('SET_SINGLE_JOB_OFFER', updated)
	if (
		updated.capabilities?.view_applicants === false ||
		updated.capabilities?.edit_job === false
	)
		commit('application/RESET_STATE', null, { root: true })
}

async function saveJobOfferDraft({ commit, rootState }, { id, data }, section) {
	const isCurrent = captureRecruitmentContext(rootState)
	try {
		const updated = await this.$axios.$patch(
			`/job-offer/${id}/${section}`,
			data,
			{ __skipGlobalErrorToast: true, __suppressValidationToast: true }
		)
		if (!isCurrent()) return null
		if (!updated) throw new Error('Job save returned no saved resource')
		applyCanonicalAccess(commit, updated)
		return updated
	} catch (error) {
		if (!isCurrent()) return null
		reportWorkflowError(error, {
			flow: 'job_offer_management',
			operation: `save_${section}`,
			jobOfferId: id,
			rootState
		})
		throw error
	}
}

export const namespaced = true

function beginJobOfferRead({ state, rootState, commit }, key) {
	const isCurrentContext = captureRecruitmentContext(rootState)
	const request = {}
	commit('SET_READ_REQUEST', { key, request })
	return () => state.readRequests[key] === request && isCurrentContext()
}

export const getDefaultState = () => ({
	jobOffers: [],
	currentJobOffer: null,
	readRequests: { list: null, detail: null, teamMembers: null },
	hiringTeamMembers: null,
	filterMode: 'all' // 'all' | 'active' | 'inactive'
})

export const state = () => getDefaultState()

export const getters = {
	jobOffers(state) {
		return state.jobOffers
	},
	jobOfferCounts: state => {
		const total = state.jobOffers.length
		const active = state.jobOffers.filter(job => job.active ?? false).length
		const inactive = total - active

		return { total, active, inactive }
	},
	/**
	 * Filters job offers based on active/inactive status and optional search query
	 * @param {string} searchQuery - Optional search string to filter by job name
	 * @returns {Array} Filtered array of job offers
	 */
	filteredJobOffers: state => searchQuery => {
		let list = state.jobOffers

		if (state.filterMode !== 'all') {
			list = list.filter(job =>
				state.filterMode === 'active'
					? job.active ?? false
					: !(job.active ?? false)
			)
		}

		if (searchQuery && searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim()
			list = list.filter(job =>
				(job.name || '').toLowerCase().includes(query)
			)
		}

		return list
	}
}

export const actions = {
	invalidateJobAccess({ commit }, id) {
		invalidateJobAccess(commit, id)
	},
	applyCanonicalAccess({ commit }, job) {
		applyCanonicalAccess(commit, job)
	},
	async searchHiringTeamMembers(context, { id, search = '', page = 1 }) {
		const isCurrent = beginJobOfferRead(context, 'teamMembers')
		let response
		try {
			response = await this.$axios.$get(
				`/job-offer/${id}/hiring-team-members`,
				{ params: { search, page }, __skipGlobalErrorToast: true }
			)
		} catch (error) {
			if (!isCurrent()) return null
			throw error
		}
		if (!isCurrent()) return null
		if (!response) throw new Error('Member search returned no response')
		context.commit('SET_HIRING_TEAM_MEMBERS', {
			jobId: id,
			search,
			members: response.data,
			nextPage: response.links?.next ? page + 1 : null,
			append: page > 1
		})
		return response
	},
	async savePermissions(context, payload) {
		return saveJobOfferDraft.call(this, context, payload, 'permissions')
	},
	async needJobOffers({ dispatch, state }) {
		if (!state.jobOffers || state.jobOffers.length === 0) {
			await dispatch('fetch')
		}

		return state.jobOffers
	},

	// Returns whether the list is trustworthy: callers that must not present a
	// failed request as "no jobs" can branch on it. Existing callers ignore it.
	async fetch(context) {
		const { commit } = context
		const isCurrent = beginJobOfferRead(context, 'list')
		try {
			const data = await this.$axios.$get(`/job-offer`)
			if (!isCurrent()) return false
			commit('SET_JOB_OFFERS', data)
			return true
		} catch (error) {
			if (!isCurrent()) return false
			if (error.response?.status === 401) {
				throw error
			}

			console.error('JobOffer fetch error:', error)

			// Handle structured error responses from backend
			if (error.response && error.response.data) {
				const errorData = error.response.data

				// Log the specific error for debugging
				console.warn('JobOffer fetch failed:', {
					status: error.response.status,
					error_code: errorData.error_code,
					message: errorData.message,
					url: error.config?.url
				})

				// Set empty array as fallback
				commit('SET_JOB_OFFERS', errorData.data || [])

				// Show user-friendly message based on error type
				if (errorData.error_code === 'UNAUTHENTICATED_USER') {
					// Handle authentication error - could redirect to login
					this.$router?.push('/auth/login')
				} else if (errorData.error_code === 'NO_CURRENT_ORGANIZATION') {
					// Handle missing organization - could redirect to organization setup
					console.warn('User has no current organization')
				}
			} else {
				// Handle unexpected errors
				console.error('Unexpected error fetching job offers:', error)
				commit('SET_JOB_OFFERS', [])
			}

			return false
		}
	},

	async fetchById(context, id) {
		const { commit } = context
		const isCurrent = beginJobOfferRead(context, 'detail')
		try {
			const response = await this.$axios.$get(`/job-offer/${id}`)
			if (!isCurrent()) return null
			commit('SET_SINGLE_JOB_OFFER', response)
			return response
		} catch (error) {
			if (!isCurrent()) return null
			if (error.response?.status === 401) {
				throw error
			}

			console.error('Error fetching job offer by ID:', error)
		}
	},

	async activate(context, payload) {
		return updateLifecycle.call(
			this,
			context,
			payload,
			{
				active: true,
				applications_paused: false,
				multiposting: Boolean(payload.multiposting)
			},
			'activate'
		)
	},

	async deactivate(context, payload) {
		return updateLifecycle.call(
			this,
			context,
			payload,
			{ active: false },
			'deactivate'
		)
	},

	async setApplicationsPaused(context, payload) {
		return updateLifecycle.call(
			this,
			context,
			payload,
			{ applications_paused: payload.applicationsPaused },
			payload.applicationsPaused
				? 'pause_applications'
				: 'resume_applications'
		)
	},

	async saveEdit(context, payload) {
		return saveJobOfferDraft.call(this, context, payload, 'edit')
	},

	async update({ dispatch, commit, rootState }, payload) {
		const isCurrentContext = captureRecruitmentContext(rootState)
		const jobOffer = payload.jobOffer || payload
		const context = payload.context || {}
		const suppressToast = payload.suppressToast || false
		const successToast = payload.successToast || null
		const throwOnError = payload.throwOnError || false

		const saveSection =
			context && context.save_section ? context.save_section : null
		const isCriteriaSave =
			saveSection === 'requirements' ||
			saveSection === 'education' ||
			saveSection === 'languages'

		// Prepare URL with query parameters if context is provided
		let url = `/job-offer/${jobOffer.id}`

		if (context.from_general) {
			url += '?from_general=true'
		}

		let languages

		if (jobOffer.languages) {
			languages = jobOffer.languages.map(language => {
				return {
					id: language.id,
					attributes: {
						niveau: language.pivot?.niveau || language.niveau
					}
				}
			})
		}

		let educationTypes

		if (jobOffer.educationTypes) {
			educationTypes = jobOffer.educationTypes.map(educationType => {
				return {
					id: educationType.id,
					attributes: {
						name: educationType.pivot?.name || educationType.name,
						end_year:
							educationType.pivot?.end_year ||
							educationType.end_year
					}
				}
			})
		}

		let data

		if (isCriteriaSave) {
			const criteriaPayload = {
				id: jobOffer.id
			}

			if (saveSection === 'requirements') {
				criteriaPayload.requirements = jobOffer.requirements
			}

			if (saveSection === 'education' && Array.isArray(educationTypes)) {
				criteriaPayload.education_types = educationTypes
			}

			if (saveSection === 'languages' && Array.isArray(languages)) {
				criteriaPayload.languages = languages
			}

			data = criteriaPayload
		} else {
			data = {
				id: jobOffer.id,
				name: jobOffer.name,
				active: jobOffer.active,
				description: jobOffer.description,
				salary: jobOffer.salary,
				salary_currency: normalizeSalaryCurrency(
					jobOffer.salary_currency
				),
				url: jobOffer.url,
				min_pensum: jobOffer.min_pensum,
				max_pensum: jobOffer.max_pensum,
				education_types: educationTypes,
				requirements: jobOffer.requirements,
				start_date: jobOffer.start_date,
				languages: languages,
				...(hasLocationFields(jobOffer)
					? serializeLocationPayload(jobOffer, { structured: true })
					: { province_id: jobOffer.province_id }),
				employment_type_id: jobOffer.employment_type_id,
				job_position_id: jobOffer.job_position_id,
				occupational_field_id: jobOffer.occupational_field_id,
				multiposting: jobOffer.multiposting,
				applications_paused: jobOffer.applications_paused
			}
		}

		if (isCriteriaSave) {
			try {
				const response = await this.$axios.request({
					method: 'patch',
					url,
					data,
					__skipGlobalErrorToast: true,
					__suppressValidationToast: true,
					__showValidationSummary: false,
					validateStatus: status =>
						(status >= 200 && status < 300) || status === 422
				})
				if (!isCurrentContext()) return null

				if (response.status === 422) {
					if (throwOnError) {
						const validationError = new Error(
							'Validation failed while updating job offer'
						)
						validationError.response = response
						throw validationError
					}
					return
				}

				const updated = response.data
				if (!suppressToast) {
					dispatch(
						'toast/push',
						successToast || {
							title:
								updated.name || this.app.i18n.t('crud.updated'),
							message: this.app.i18n.t('crud.updated-text'),
							type: 'success'
						},
						{ root: true }
					)
				}
				commit('SET_JOB_OFFER_BY_ID', updated)
				trackPersonalityProfileDefined({
					app: this.app,
					rootState,
					context,
					jobOffer
				})
				return updated
			} catch (error) {
				if (!isCurrentContext()) return null
				console.error('Error updating job offer:', error)
				reportWorkflowError(error, {
					flow: 'job_offer_management',
					operation: `save_${saveSection || 'criteria'}`,
					jobOfferId: jobOffer.id,
					rootState
				})
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('general.error'),
						message: this.app.i18n.t('crud.update-failed'),
						type: 'danger'
					},
					{ root: true }
				)

				if (throwOnError) {
					throw error
				}
			}
			return
		}

		return await this.$axios
			.$patch(url, data, { __skipGlobalErrorToast: true })
			.then(data => {
				if (!isCurrentContext()) return null
				if (!suppressToast) {
					dispatch(
						'toast/push',
						successToast || {
							title: data.name || this.app.i18n.t('crud.updated'),
							message: this.app.i18n.t('crud.updated-text'),
							type: 'success'
						},
						{ root: true }
					)
				}
				commit('SET_JOB_OFFER_BY_ID', data)
				trackPersonalityProfileDefined({
					app: this.app,
					rootState,
					context,
					jobOffer
				})
				return data
			})
			.catch(error => {
				if (!isCurrentContext()) return null
				console.error('Error updating job offer:', error)

				const statusCode = error.response?.status
				if (statusCode === 422) {
					if (throwOnError) {
						throw error
					}
					return
				}

				reportWorkflowError(error, {
					flow: 'job_offer_management',
					operation: `update_${saveSection || 'job_offer'}`,
					jobOfferId: jobOffer.id,
					rootState
				})

				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('general.error'),
						message: this.app.i18n.t('crud.update-failed'),
						type: 'danger'
					},
					{ root: true }
				)

				if (throwOnError) {
					throw error
				}
			})
	},

	async submitAssessmentResult({ rootState }, payload) {
		const {
			jobOfferId,
			personality,
			roundData,
			teamAssessmentMode = false,
			context = {}
		} = payload
		const endpoint = teamAssessmentMode
			? `/job-offer/${jobOfferId}/team-assessment-result`
			: `/job-offer/${jobOfferId}/assessment-result`
		let result

		try {
			const response = await this.$axios.$post(
				endpoint,
				{
					personality_round_data: roundData
				},
				{ __skipGlobalErrorToast: true }
			)
			result = response.data
		} catch (error) {
			const attributedEndpointIsUnavailable = [404, 405].includes(
				error.response?.status
			)

			if (teamAssessmentMode || !attributedEndpointIsUnavailable) {
				reportWorkflowError(error, {
					flow: 'job_offer_assessment',
					operation: 'submit_result',
					jobOfferId,
					rootState,
					teamAssessmentMode,
					reportValidationError: true
				})
				throw error
			}

			try {
				result = await this.$axios.$patch(
					`/job-offer/${jobOfferId}`,
					{
						personality,
						personality_round_data: roundData
					},
					{ __skipGlobalErrorToast: true }
				)
			} catch (fallbackError) {
				reportWorkflowError(fallbackError, {
					flow: 'job_offer_assessment',
					operation: 'submit_result_legacy_fallback',
					jobOfferId,
					rootState,
					reportValidationError: true
				})
				throw fallbackError
			}
		}

		if (!teamAssessmentMode) {
			trackPersonalityProfileDefined({
				app: this.app,
				rootState,
				context,
				jobOffer: {
					id: jobOfferId,
					personality: result.personality,
					personality_round_data: result.personality_round_data
				}
			})
		}

		return result
	},

	async submitManualAssessmentResult({ rootState }, payload) {
		const { jobOfferId, personality, context = {} } = payload
		let response

		try {
			response = await this.$axios.$post(
				`/job-offer/${jobOfferId}/manual-assessment-result`,
				{
					personality
				},
				{ __skipGlobalErrorToast: true }
			)
		} catch (error) {
			reportWorkflowError(error, {
				flow: 'job_offer_assessment',
				operation: 'submit_manual_result',
				jobOfferId,
				rootState,
				reportValidationError: true
			})
			throw error
		}

		trackPersonalityProfileDefined({
			app: this.app,
			rootState,
			context,
			jobOffer: {
				id: jobOfferId,
				personality: response.data.personality,
				personality_round_data: response.data.personality_round_data
			}
		})

		return response.data
	},

	async publishFromReview({ commit, dispatch, rootState }, payload) {
		const isCurrentContext = captureRecruitmentContext(rootState)
		const jobOffer = payload.jobOffer || payload
		const sourceSurface = payload.sourceSurface || 'job_offer_review'
		const suppressSuccessToast = Boolean(payload.suppressSuccessToast)
		const jobName = jobOffer?.name || ''

		let updated

		try {
			updated = await this.$axios.$patch(
				`/job-offer/${jobOffer.id}/creation-review-publish`,
				{
					active: true,
					applications_paused: false,
					multiposting: !!jobOffer?.multiposting
				},
				{ __skipGlobalErrorToast: true }
			)
		} catch (error) {
			if (!isCurrentContext()) return null
			reportWorkflowError(error, {
				flow: 'job_offer_creation',
				operation: 'publish',
				jobOfferId: jobOffer.id,
				rootState
			})
			throw error
		}

		if (!isCurrentContext()) return null
		if (!suppressSuccessToast) {
			dispatch(
				'toast/push',
				{
					title: this.app.i18n.t(
						'job-offer.toast.activate-job.heading',
						{
							job: jobName
						}
					),
					message: jobOffer?.multiposting
						? hasOrganizationLinkedInCompany(
								rootState.user?.user?.current_organization
						  )
							? this.app.i18n.t(
									'job-offer.toast.activate-job.body.with-linkedin',
									{ job: jobName }
							  )
							: this.app.i18n.t(
									'job-offer.toast.activate-job.body.posted-without-linkedin',
									{ job: jobName }
							  )
						: this.app.i18n.t(
								'job-offer.toast.activate-job.body.without-linkedin',
								{ job: jobName }
						  ),
					type: 'success'
				},
				{ root: true }
			)
		}

		commit('SET_JOB_OFFER_BY_ID', updated)

		if (updated?.active && process.client && this.app.$analytics) {
			const user = rootState.user?.user
			const organizationId = user?.current_organization?.id
			if (organizationId) {
				this.app.$analytics.trackJobPublished({
					organizationId,
					userId: user?.id,
					jobOfferId: jobOffer?.id,
					sourceSurface
				})
			}
		}

		return updated
	},

	async store({ commit, rootState }, payload) {
		const isCurrentContext = captureRecruitmentContext(rootState)
		// Extract item and context from payload
		const item = payload.jobOffer || payload
		const context = payload.context || {}

		// Prepare URL with query parameters if context is provided
		let url = '/job-offer'
		if (context.from_general) {
			url += '?from_general=true'
		}
		const data = hasLocationFields(item)
			? {
					...item,
					...serializeLocationPayload(item, { structured: true })
			  }
			: item

		try {
			const created = await this.$axios.$post(url, data, {
				__skipGlobalErrorToast: true
			})
			if (!isCurrentContext()) return null
			commit('ADD_JOB_OFFER', created)
			return created
		} catch (error) {
			if (!isCurrentContext()) return null
			reportWorkflowError(error, {
				flow: 'job_offer_creation',
				operation: 'create_draft',
				rootState
			})
			throw error
		}
	},

	async delete({ commit, dispatch, rootState }, payload) {
		const isCurrentContext = captureRecruitmentContext(rootState)
		const id = payload?.id || payload
		const successToast = payload?.successToast || null

		try {
			await this.$axios.$delete(`/job-offer/${id}`, {
				__skipGlobalErrorToast: true
			})
			if (!isCurrentContext()) return null
			commit('REMOVE_JOB_OFFER', id)
			await dispatch(
				'toast/push',
				successToast || {
					title: this.app.i18n.t('crud.deleted'),
					message: this.app.i18n.t('crud.successfully-deleted'),
					type: 'success'
				},
				{ root: true }
			)
			return true
		} catch (error) {
			if (!isCurrentContext()) return null
			console.error('Error deleting job offer:', error)
			dispatch(
				'toast/push',
				{
					title: this.app.i18n.t('general.error'),
					message: this.app.i18n.t('general.unexpected-error'),
					type: 'danger'
				},
				{ root: true }
			)
			throw error
		}
	},

	async duplicate({ commit, dispatch, rootState }, id) {
		const isCurrentContext = captureRecruitmentContext(rootState)
		try {
			const response = await this.$axios.$post(
				`/job-offer/${id}/duplicate`,
				null,
				{ __skipGlobalErrorToast: true }
			)
			if (!isCurrentContext()) return null
			commit('ADD_JOB_OFFER', response)
			await dispatch(
				'toast/push',
				{
					title: this.app.i18n.t('crud.duplicated'),
					message: this.app.i18n.t('crud.duplicated-text'),
					type: 'success'
				},
				{ root: true }
			)
			return response
		} catch (error) {
			if (!isCurrentContext()) return null
			console.error('Error duplicating job offer:', error)
			dispatch(
				'toast/push',
				{
					title: this.app.i18n.t('general.error'),
					message: this.app.i18n.t('general.unexpected-error'),
					type: 'danger'
				},
				{ root: true }
			)
			throw error
		}
	},
	setFilterMode({ commit }, mode) {
		commit('SET_FILTER_MODE', mode)
	},
	reset({ commit }) {
		commit('RESET_STATE')
	}
}

export const mutations = {
	SET_HIRING_TEAM_MEMBERS(state, result) {
		const previous = state.hiringTeamMembers
		state.hiringTeamMembers = {
			...result,
			members:
				result.append &&
				previous?.jobId === result.jobId &&
				previous?.search === result.search
					? [...previous.members, ...result.members]
					: result.members
		}
	},
	UPDATE_JOB_OFFER_ACCESS(state, access) {
		const canApply = job =>
			access.access_version >= (job.access_version ?? 0)
		const fields = [
			'access_version',
			'access_updated_at',
			'hiring_team',
			'pending_hiring_team'
		]
		const changes = Object.fromEntries(
			fields
				.filter(field => field in access)
				.map(field => [field, access[field]])
		)
		const index = state.jobOffers.findIndex(job => job.id === access.id)
		if (index !== -1 && canApply(state.jobOffers[index])) {
			Vue.set(state.jobOffers, index, {
				...state.jobOffers[index],
				...changes
			})
		}
		if (
			state.currentJobOffer?.id === access.id &&
			canApply(state.currentJobOffer)
		) {
			state.currentJobOffer = { ...state.currentJobOffer, ...changes }
		}
	},
	SET_READ_REQUEST(state, { key, request }) {
		state.readRequests[key] = request
	},
	UPDATE_JOB_OFFER_FIELDS(state, { id, changes }) {
		const index = state.jobOffers.findIndex(job => job.id === id)

		if (index !== -1) {
			const updated = {
				...state.jobOffers[index],
				...changes
			}
			Vue.set(state.jobOffers, index, updated)
		}
	},
	SET_JOB_OFFER_BY_ID(state, jobOffer) {
		const index = state.jobOffers.findIndex(job => job.id === jobOffer.id)

		if (index !== -1) {
			Vue.set(state.jobOffers, index, jobOffer)
		}
	},
	SET_SINGLE_JOB_OFFER(state, offer) {
		state.currentJobOffer = offer
	},
	ADD_JOB_OFFER(state, offer) {
		state.jobOffers.push(offer)
	},
	REMOVE_JOB_OFFER(state, id) {
		state.jobOffers = state.jobOffers.filter(job => job.id !== id)
		if (state.hiringTeamMembers?.jobId === id)
			state.hiringTeamMembers = null
	},
	SET_JOB_OFFERS(state, offers) {
		state.jobOffers = offers
	},
	SET_FILTER_MODE(state, mode) {
		state.filterMode = mode
	},
	RESET_STATE(state) {
		Object.assign(state, getDefaultState())
	}
}
